/* eslint-disable no-console */

import { Injectable, DOCUMENT, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import type { SegmentContactTraits } from '@ninety/platform/models/analytics/segment/segment-contact-traits';
import type {
  SegmentGroupTraits,
  SegmentReservedGroupTraits,
} from '@ninety/platform/models/analytics/segment/segment-group-traits';
import { environment } from '@ninety/ui/web/environments';

import { isAutomatedTestSession } from './automated-test-session';

// Global variable injected by analytics script
// https://segment.com/docs/getting-started/
// @ts-ignore
declare let analytics: SegmentAnalytics.AnalyticsJS;

@Injectable({
  providedIn: 'root',
})
export class SegmentService {
  private document = inject<Document>(DOCUMENT);

  private isLoaded$ = new BehaviorSubject<boolean>(false);
  private pastFirstPageCall = false;
  private scriptRequested = false;

  constructor() {
    this.installAnalyticsStub();
  }

  loaded(): Observable<boolean> {
    return this.isLoaded$.asObservable();
  }

  loadScript(): void {
    if (this.scriptRequested) return;
    this.scriptRequested = true;

    // Automated sign-ups must not reach Segment. The stub installed in the constructor is what makes
    // this safe: every analytics.* call only pushes onto an array, and nothing but the real
    // analytics.min.js ever drains it. Skipping the load therefore strands the queue — the effects
    // still run and still call track/identify/page, but nothing leaves the browser and nothing
    // throws, so no caller needs a guard.
    if (isAutomatedTestSession()) {
      console.debug('[Segment] automated test session, skipping analytics.js');
      return;
    }

    const w = window as any;
    if (w.analytics?.initialize) {
      this.isLoaded$.next(true);
      return;
    }

    this.document.addEventListener('pageOnLoad', () => (this.pastFirstPageCall = true));

    w.analytics.load(environment.segmentApiKey);

    // See DEV-13164 for context
    if ('/login' === window.location.pathname) {
      console.debug('[Segment] ignored login path, waiting to call page after successful login');
    } else if (!this.pastFirstPageCall) {
      this.document.dispatchEvent(new CustomEvent('pageOnLoad'));
      console.debug('[Segment] found non-login path, calling page');
      w.analytics.page();
    }

    this.isLoaded$.next(true);
  }

  // Installs Segment's standard analytics stub at bootstrap, before the real script loads.
  // Each method (ready, identify, track, etc.) pushes [methodName, ...args] onto an array.
  // When analytics.load(key) fetches the real analytics.min.js, it drains this queue and
  // replays every call in order — no events are lost, just deferred.
  private installAnalyticsStub(): void {
    const w = window as any;
    if (w.analytics?.initialize || w.analytics?.invoked) return;

    const a: any = (w.analytics = w.analytics || []);
    a.invoked = true;
    a.methods = [
      'trackSubmit',
      'trackClick',
      'trackLink',
      'trackForm',
      'pageview',
      'identify',
      'reset',
      'group',
      'track',
      'ready',
      'alias',
      'debug',
      'page',
      'once',
      'off',
      'on',
      'addSourceMiddleware',
      'addIntegrationMiddleware',
      'setAnonymousId',
      'addDestinationMiddleware',
    ];
    a.factory = function (method: string) {
      return function (...args: any[]) {
        args.unshift(method);
        a.push(args);
        return a;
      };
    };
    for (let i = 0; i < a.methods.length; i++) {
      a[a.methods[i]] = a.factory(a.methods[i]);
    }
    const doc = this.document;
    a.load = function (key: string, opts?: any) {
      const script = doc.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = 'https://cdn.segment.com/analytics.js/v1/' + key + '/analytics.min.js';
      const first = doc.getElementsByTagName('script')[0];
      if (first?.parentNode) {
        first.parentNode.insertBefore(script, first);
      } else {
        doc.head.appendChild(script);
      }
      a._loadOptions = opts;
    };
    a.SNIPPET_VERSION = '4.13.1';
  }

  /**
   * Called when a navigation event occurs
   *
   * Note: Conditionally called by segment on initial script load. See [DEV-13164](https://traxion.atlassian.net/browse/DEV-13164)
   *
   * Reference:
   * https://segment.com/docs/connections/spec/page/
   */
  page(): void {
    try {
      analytics.page();
    } catch (error) {
      console.error('[Segment] analytics.page error: ', error);
    }
  }

  /**
   * Called when the company user changes (IE when a user logs in). Used to properly identify the user to segment and its dependencies. For
   * context into why page is called here, see DEV-13164.
   */
  onUserChange(contactTraits: SegmentContactTraits, groupTraits: SegmentGroupTraits): void {
    if (!this.pastFirstPageCall) {
      this.pastFirstPageCall = true;
      this.page();
    }

    this.identify(contactTraits);
    this.group(groupTraits);
  }

  /**
   * Allows Segment to tie a user to their actions and record traits about
   * them. It includes a unique User ID and any optional traits you know
   * about the user, like their email, name, and more.
   *
   * Reference:
   * https://segment.com/docs/connections/spec/identify/
   */
  identify(contact: SegmentContactTraits): void {
    try {
      analytics.identify(contact.id, contact);
    } catch (e) {
      console.error(`[Segment] Error creating contact association for contact id ${contact?.id}`, e);
    }
  }

  group(group: SegmentReservedGroupTraits | SegmentGroupTraits): void {
    try {
      analytics.group(group.id, group);
    } catch (e) {
      console.error(`[Segment] Error creating group association for group id ${group?.id}`, e);
    }
  }

  /**
   * Reference:
   * https://segment.com/docs/connections/spec/track/#properties
   */
  track<T>(event: string, fields?: T) {
    try {
      analytics.track(event, fields);
    } catch (error) {
      console.error(`[Segment] Error tracking ${event} event: `, error);
    }
  }
}
